import React, { useCallback } from 'react';

import { Checkbox } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { isEqual } from 'lodash';

import {
  ActionsRequisesDtoActionsRequisesEnum,
  TraitementResponseDtoListStatutEnum,
} from 'lib_api/lib/api/gen';

import { FilterButton } from 'components/WrappedComponents';
import DashboardSearchBarWithTooltip from 'components/WrappedComponents/SearchBar/DashboardSearchBarWithTooltip';
import { useGFFilterContext } from 'hooks/GFFilterStoreContext';

import { FilterFormGardien } from './FilterFormGardien';
import { FilterDashboardGardien, FormFilterGardien } from './types';

import 'styles/DashboardHeaderFilter.less';

interface Props {
  setFilter: (value: Partial<FilterDashboardGardien>) => void;
  filter: Partial<FilterDashboardGardien>;
}

function keepDossierSortis(
  statut: TraitementResponseDtoListStatutEnum[],
  sortieFilter: boolean,
  motifDeSortieFilter: string[],
  includeDossiersAnonymises: boolean,
  includeOnlyNotDetruitApresSorti: boolean,
): boolean {
  return (
    statut.includes(TraitementResponseDtoListStatutEnum.SORTI) ||
    (statut.length === 0 && sortieFilter) ||
    motifDeSortieFilter.length !== 0 ||
    // if we search for anonymized dossiers
    includeDossiersAnonymises === true ||
    // if we search for dossiers not destroyed after sortie
    includeOnlyNotDetruitApresSorti === true
  );
}

export function Filter({ filter, setFilter }: Props): React.ReactElement {
  const {
    search,
    includeDossiersSortis,
    includeOnlyActionsRequises,
    ...formFilter
  } = filter;
  const [, , gfActionsRequises] = useGFFilterContext();

  const keepOnlyActionsRequises = useCallback(
    (actionsRequises?: ActionsRequisesDtoActionsRequisesEnum[]): boolean => {
      return isEqual(new Set(actionsRequises), new Set(gfActionsRequises));
    },
    [gfActionsRequises],
  );

  function setFormFilter(value: Partial<FormFilterGardien>) {
    const {
      statut,
      motifDeSortie,
      actionsRequises,
      includeDossiersAnonymises,
      includeOnlyNotDetruitApresSorti,
    } = value;
    setFilter({
      ...filter,
      ...value,
      includeDossiersSortis: keepDossierSortis(
        statut ?? [],
        filter.includeDossiersSortis ?? false,
        motifDeSortie ?? [],
        includeDossiersAnonymises ?? false,
        includeOnlyNotDetruitApresSorti ?? false,
      ),
      includeOnlyActionsRequises: keepOnlyActionsRequises(actionsRequises),
      includeOnlyNotDetruitApresSorti,
    });
  }

  function setIncludeDossiersSortis(event: CheckboxChangeEvent) {
    const { statut, motifDeSortie, includeOnlyNotDetruitApresSorti } = filter;
    const updatedStatut =
      statut?.filter(s => s !== TraitementResponseDtoListStatutEnum.SORTI) ||
      [];

    // Only add the SORTI statut filter if the statut filter is not empty
    if (event.target.checked && (statut?.length ?? -1) > 0) {
      updatedStatut.push(TraitementResponseDtoListStatutEnum.SORTI);
    }

    setFilter({
      ...filter,
      statut: updatedStatut,
      motifDeSortie: event.target.checked ? motifDeSortie : [],
      includeDossiersSortis: event.target.checked,
      includeDossiersAnonymises: undefined,
      includeOnlyNotDetruitApresSorti: event.target.checked
        ? includeOnlyNotDetruitApresSorti
        : undefined,
    });
  }

  function setIncludeOnlyActionsRequises(event: CheckboxChangeEvent) {
    const isChecked = event.target.checked;
    setFilter({
      ...filter,
      actionsRequises: isChecked ? gfActionsRequises : undefined,
      includeOnlyActionsRequises: isChecked,
    });
  }

  return (
    <div className="dashboard-header-filter">
      <DashboardSearchBarWithTooltip
        search={search}
        filter={filter}
        setFilter={setFilter}
      />
      <FilterButton
        setFilter={setFormFilter}
        filter={formFilter}
        textInactiveFilter={'Filtres'}
        textActiveFilter={'Filtres (actifs)'}
      >
        {(setter, data) => (
          <FilterFormGardien setFilter={setter} filter={data} />
        )}
      </FilterButton>
      <Checkbox
        checked={includeDossiersSortis}
        onChange={setIncludeDossiersSortis}
      >
        Afficher les véhicules sortis
      </Checkbox>
      <Checkbox
        checked={includeOnlyActionsRequises}
        onChange={setIncludeOnlyActionsRequises}
      >
        Afficher uniquement les actions requises
      </Checkbox>
    </div>
  );
}
